import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import { 
  CheckCircle, 
  Calendar, 
  DollarSign, 
  Target, 
  Users, 
  BarChart3,
  Share2,
  Download,
  Eye
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

function CampaignSuccess() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pega os dados da campanha do state da navegação
  const campaignData = location.state?.campaignData;

  useEffect(() => {
    // Se não houver dados da campanha, redireciona para criar campanha
    if (!campaignData) {
      navigate('/create-campaign', { replace: true });
    }
  }, [campaignData, navigate]);

  // Se não houver dados, retorna null (o useEffect redirecionará)
  if (!campaignData) {
    return null;
  }

  const campaign = {
    id: campaignData.id || 'CAMP-' + Date.now(),
    name: campaignData.name || 'Nova Campanha',
    objective: campaignData.objective || 'Reconhecimento de Marca',
    status: 'Ativa',
    budget: campaignData.budget || 0,
    budgetType: campaignData.budgetType || 'daily',
    startDate: campaignData.startDate || new Date().toISOString().split('T')[0],
    endDate: campaignData.endDate || new Date().toISOString().split('T')[0],
    locations: campaignData.locations || [],
    interests: campaignData.interests || [],
    creativesCount: campaignData.creativesCount || 0,
    estimatedReach: campaignData.estimatedReach || '10.000 - 50.000'
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleViewCampaign = () => {
    navigate(`/campaigns/${campaign.id}`);
  };

  const handleCreateAnother = () => {
    navigate('/create-campaign');
  };

  const handleShare = () => {
    // Lógica para compartilhar
    if (navigator.share) {
      navigator.share({
        title: campaign.name,
        text: `Confira minha nova campanha: ${campaign.name}`,
        url: window.location.origin + `/campaigns/${campaign.id}`
      }).catch(err => console.log('Erro ao compartilhar:', err));
    } else {
      alert('Compartilhamento não disponível neste navegador');
    }
  };

  const handleDownloadReport = () => {
    // Lógica para baixar relatório
    alert('Download do relatório será implementado');
  };

  const calculateDuration = () => {
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header de Sucesso */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          mb: 4, 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'scaleIn 0.5s ease-out'
            }}
          >
            <CheckCircle size={48} />
          </Box>
        </Box>
        
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
          Campanha Criada com Sucesso!
        </Typography>
        
        <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
          Sua campanha já está ativa e começará a ser exibida em breve
        </Typography>

        <Chip 
          label={`ID: ${campaign.id}`}
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      </Paper>

      {/* Alert de Confirmação */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <strong>Próximos passos:</strong> Acompanhe as métricas da sua campanha em tempo real no dashboard.
      </Alert>

      {/* Resumo da Campanha */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Target size={24} color="#667eea" />
          Resumo da Campanha
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Informações Básicas */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  NOME DA CAMPANHA
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {campaign.name}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
                  OBJETIVO
                </Typography>
                <Chip label={campaign.objective} color="primary" size="small" />

                <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
                  STATUS
                </Typography>
                <Chip 
                  label={campaign.status} 
                  color="success" 
                  size="small"
                  icon={<CheckCircle size={16} />}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Orçamento */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <DollarSign size={20} color="#4CAF50" />
                  <Typography variant="subtitle2" color="textSecondary">
                    ORÇAMENTO
                  </Typography>
                </Box>
                
                <Typography variant="h4" color="success.main" gutterBottom>
                  R$ {campaign.budget.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {campaign.budgetType === 'daily' ? 'Por dia' : 'Total da campanha'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
                  <Calendar size={20} color="#2196F3" />
                  <Typography variant="subtitle2" color="textSecondary">
                    PERÍODO
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {new Date(campaign.startDate).toLocaleDateString('pt-BR')} até {new Date(campaign.endDate).toLocaleDateString('pt-BR')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {calculateDuration()} dias
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Segmentação */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Users size={20} color="#FF9800" />
                  <Typography variant="subtitle2" color="textSecondary">
                    SEGMENTAÇÃO
                  </Typography>
                </Box>

                {campaign.locations.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Localizações ({campaign.locations.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {campaign.locations.map((loc, index) => (
                        <Chip key={index} label={loc} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </>
                )}

                {campaign.interests.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Interesses ({campaign.interests.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {campaign.interests.map((interest, index) => (
                        <Chip key={index} label={interest} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </>
                )}

                {campaign.locations.length === 0 && campaign.interests.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Sem segmentação específica
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Métricas Estimadas */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BarChart3 size={20} color="#9C27B0" />
                  <Typography variant="subtitle2" color="textSecondary">
                    ESTIMATIVAS
                  </Typography>
                </Box>

                <Typography variant="subtitle2" gutterBottom>
                  Alcance Estimado:
                </Typography>
                <Typography variant="h6" color="primary" gutterBottom>
                  {campaign.estimatedReach} pessoas
                </Typography>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Criativos Ativos:
                </Typography>
                <Typography variant="h6">
                  {campaign.creativesCount} criativo{campaign.creativesCount > 1 ? 's' : ''}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Próximos Passos */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>
          📋 Próximos Passos
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                1. Acompanhe o Desempenho
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Acesse o dashboard para ver métricas em tempo real da sua campanha
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                2. Otimize os Resultados
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Ajuste seu orçamento e segmentação conforme o desempenho
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                3. Crie Relatórios
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Gere relatórios detalhados sobre o desempenho da campanha
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Ações */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<BarChart3 />}
          onClick={handleGoToDashboard}
          sx={{ 
            bgcolor: '#667eea',
            '&:hover': { bgcolor: '#5568d3' }
          }}
        >
          Ir para Dashboard
        </Button>

        <Button
          variant="outlined"
          size="large"
          startIcon={<Eye />}
          onClick={handleViewCampaign}
        >
          Ver Detalhes
        </Button>

        <Button
          variant="outlined"
          size="large"
          startIcon={<Share2 />}
          onClick={handleShare}
        >
          Compartilhar
        </Button>

        <Button
          variant="outlined"
          size="large"
          startIcon={<Download />}
          onClick={handleDownloadReport}
        >
          Baixar Relatório
        </Button>

        <Button
          variant="text"
          size="large"
          onClick={handleCreateAnother}
        >
          Criar Outra Campanha
        </Button>
      </Box>

      {/* Animação CSS */}
      <style>{`
        @keyframes scaleIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </Box>
  );
}

export default CampaignSuccess;